/**
 * Validation utilities for credit card expiry dates
 */

export interface ExpiryValidationResult {
  isValid: boolean;
  error: string | null;
}

/**
 * Validates a credit card expiry date
 * @param month - Expiry month (1-12)
 * @param year - Expiry year (4 digits)
 * @returns Validation result with error message if invalid
 */
export function validateExpiry(month: number, year: number): ExpiryValidationResult {
  // Check for empty, null, undefined, or zero values
  if (!month || !year || month === 0 || year === 0) {
    return {
      isValid: false,
      error: "Your card's expiration date is incomplete.",
    };
  }

  if (Number.isNaN(month) || Number.isNaN(year)) {
    return {
      isValid: false,
      error: "Your card's expiration date is invalid.",
    };
  }

  if (month < 1 || month > 12) {
    return {
      isValid: false,
      error: "Your card's expiration month must be between 01 and 12.",
    };
  }

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // JavaScript months are 0-indexed

  if (year < currentYear) {
    return {
      isValid: false,
      error: "Your card's expiration year is in the past.",
    };
  }

  if (year === currentYear && month < currentMonth) {
    return {
      isValid: false,
      error: "Your card's expiration month is in the past.",
    };
  }

  if (year > currentYear + 20) {
    return {
      isValid: false,
      error: "Your card's expiration year is too far in the future.",
    };
  }

  return {
    isValid: true,
    error: null,
  };
}

/**
 * Formats an expiry date as MM/YYYY
 * @param month - Expiry month
 * @param year - Expiry year
 * @returns Formatted string or empty string if invalid
 */
export function formatExpiryDate(month: number | null, year: number | null): string {
  if (!month || !year) {
    return '';
  }
  return `${String(month).padStart(2, '0')}/${year}`;
}

/**
 * Parses expiry input string to extract month and year
 * @param input - Input string (e.g., "12/2025")
 * @returns Object containing parsed month and year
 */
export function parseExpiryInput(input: string): { month: number; year: number } {
  const digitsOnly = input.replace(/\D/g, '');

  let month = 0;
  let year = 0;

  if (digitsOnly.length >= 2) {
    month = parseInt(digitsOnly.slice(0, 2), 10);
  }

  if (digitsOnly.length > 2) {
    year = parseInt(digitsOnly.slice(2, 6), 10);
  }

  return { month, year };
}

/**
 * Formats raw input string as MM/YYYY
 * @param input - Raw input string
 * @returns Formatted string
 */
export function formatExpiryInput(input: string, isDeleting = false): string {
  const digitsOnly = input.replace(/\D/g, '');

  if (digitsOnly.length === 0) {
    return '';
  }

  let formatted = digitsOnly.slice(0, 2);
  if (digitsOnly.length > 2) {
    formatted += `/${digitsOnly.slice(2, 6)}`;
  } else if (digitsOnly.length === 2 && !isDeleting) {
    formatted += '/';
  }

  return formatted;
}
